import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

const DetailBlock = styled.div`
  ${tw`rounded-lg border border-gray-100 bg-gray-50 p-4 text-sm text-gray-700 space-y-4`}
`;

const DetailSection = styled.div`
  ${tw`space-y-1.5`}
`;

const DetailHeading = styled.h3`
  ${tw`text-sm font-semibold text-gray-900`}
`;

const DetailList = styled.ul`
  ${tw`list-disc pl-5 space-y-1 text-gray-600`}
`;

export const PLATFORM_SITES_PAGE_TITLE = 'Platform websites';

export const PLATFORM_SITES_PAGE_SUBTITLE =
  'List the website addresses that belong to your platform (for example autopilotee.com). This helps guests who book in your mobile app stay in the app instead of being sent to a browser.';

export const PLATFORM_SITES_CARD_LABEL = 'Listed websites';

export const PLATFORM_SITES_EDIT_BUTTON = 'Edit websites';

export const PLATFORM_SITES_MODAL_TITLE = 'Edit platform websites';

export const PLATFORM_SITES_FORM_LABEL = 'Website addresses';

export const PLATFORM_SITES_FORM_FIELD_HELPER =
  'Add every domain that is your platform’s own rental site — not a host’s separate branded site.';

export function PlatformSiteMarkersDetailGuide() {
  return (
    <DetailBlock>
      <DetailSection>
        <DetailHeading>Why this list exists</DetailHeading>
        <p>
          Some car owners run their own rental website in addition to listing on your platform. When
          a guest books that owner’s car, the app or web may send them to the owner’s site to finish
          checkout.
        </p>
        <p>
          The addresses you list here are <strong>your platform’s websites</strong>. If an owner
          only has these addresses on their profile, we treat them as a platform host — not a
          separate branded site — so guests are <strong>not redirected away</strong>.
        </p>
      </DetailSection>

      <DetailSection>
        <DetailHeading>Mobile app</DetailHeading>
        <ul className="list-none pl-0 space-y-1 text-gray-600">
          <li>
            • Owner’s profile shows <strong>only</strong> addresses from this list → guest stays in
            the app to book.
          </li>
          <li>
            • Owner has a <strong>different</strong> website (for example their own domain) → guest
            may open that site in the browser to complete checkout.
          </li>
        </ul>
      </DetailSection>

      <DetailSection>
        <DetailHeading>Web</DetailHeading>
        <p>
          The same rules apply when a guest browses on one of your platform domains. Owners with
          only platform addresses stay on your site; owners with their own branded domain may
          redirect guests to that domain for checkout (referral rules still apply).
        </p>
      </DetailSection>

      <DetailSection>
        <DetailHeading>What to enter</DetailHeading>
        <DetailList>
          <li>
            Use the domain only, such as <code className="text-gray-800">autopilotee.com</code> or{' '}
            <code className="text-gray-800">localhost:3080</code> for local testing.
          </li>
          <li>
            Include every domain where your platform runs (production, staging, local dev if
            needed).
          </li>
          <li>
            Do <strong>not</strong> add a host’s personal branded rental site here — those belong on
            the host’s Referral settings.
          </li>
        </DetailList>
      </DetailSection>

      <DetailSection>
        <DetailHeading>How entries are saved</DetailHeading>
        <p className="text-gray-600">
          We automatically clean up what you type: <code>http://</code>, <code>www.</code>, paths,
          and trailing slashes are removed so matching stays consistent.
        </p>
      </DetailSection>
    </DetailBlock>
  );
}
